import React, { useState, useCallback, useMemo } from 'react';
import {
  useRefresh,
  useNotify,
  useGetList,
  useGetOne,
} from 'react-admin';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import { getCurrencySymbol } from '@src/utils/currency';

import type { FacetGroup } from '@src/facets';
import type { ConditionOption } from '@src/conditions';
import type { PartnerSettings } from '@src/settings';
import { requirePartner } from '@src/providers/dataProvider/shared';
import type { PricingRuleInput, PricingType } from './types';
import { startPricingImport } from './startPricingImport';
import { addToPricingImport } from './addToPricingImport';
import { parseTsvFile, validateRules } from './importUtils';
import type { ParsedRule } from './importUtils';

interface ConditionDiscountsImportDialogProps {
  open: boolean;
  onClose: () => void;
  pricingType: PricingType;
}

type ImportPhase = 'idle' | 'starting' | 'importing' | 'complete';

const ConditionDiscountsImportDialog = ({ open, onClose, pricingType }: ConditionDiscountsImportDialogProps) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const isConditionType = pricingType === 'condition';
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');
  const { data: conditions = [], isLoading: conditionsLoading } = useGetList<ConditionOption>('conditions', {}, { enabled: isConditionType });

  const [file, setFile] = useState<File | null>(null);
  const [parsedRules, setParsedRules] = useState<ParsedRule[]>([]);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [importPhase, setImportPhase] = useState<ImportPhase>('idle');
  const [importResult, setImportResult] = useState<{ created: number, errors: number }>({ created: 0, errors: 0 });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (isConditionType && conditionsLoading) {
      notify('Condition codes are still loading — please wait and try again', { type: 'warning' });
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const { rules: parsed, errors: parseErrors } = parseTsvFile(content, pricingType);
      if (parsed.length === 0 && parseErrors.length > 0) {
        notify(parseErrors[0], { type: 'error' });
        setFile(null);
        return;
      }
      const { rules: validated } = validateRules(parsed, pricingType, facetGroups, conditions);
      setParsedRules(validated);
    };
    reader.onerror = () => {
      notify('Failed to read the selected file. Please try again.', { type: 'error' });
      setFile(null);
    };
    reader.readAsText(selectedFile);
  }, [conditions, conditionsLoading, facetGroups, isConditionType, notify, pricingType]);

  const handleClose = useCallback(() => {
    setFile(null);
    setParsedRules([]);
    setShowErrorsOnly(false);
    setImportPhase('idle');
    setImportResult({ created: 0, errors: 0 });
    onClose();
  }, [onClose]);

  const handleImport = useCallback(async () => {
    const validRules = parsedRules
      .filter(r => !r._hasError)
      .map((r) => {
        const cleanRule: PricingRuleInput = {};

        // Shared fields for both pricing types
        if (r.discount !== undefined) cleanRule.discount = r.discount;
        if (r.department) cleanRule.department = r.department;
        if (r.category) cleanRule.category = r.category;
        if (r.merchantCategory) cleanRule.merchantCategory = r.merchantCategory;
        if (r.tag) cleanRule.tag = r.tag;
        if (r.name) cleanRule.name = r.name;

        // Type-specific fields
        if (pricingType === 'condition') {
          if (r.condition) cleanRule.condition = r.condition;
        } else {
          if (r.sourceColumn) cleanRule.sourceColumn = r.sourceColumn;
          if (r.secondaryTag) cleanRule.secondaryTag = r.secondaryTag;
          if (r.secondaryName) cleanRule.secondaryName = r.secondaryName;
          if (r.tertiaryTag) cleanRule.tertiaryTag = r.tertiaryTag;
          if (r.tertiaryName) cleanRule.tertiaryName = r.tertiaryName;
          if (r.upc) cleanRule.upc = r.upc;
        }

        return cleanRule;
      });

    if (validRules.length === 0) {
      notify('No valid rules to import', { type: 'warning' });
      return;
    }

    try {
      const partner = requirePartner();

      // Phase 1: Start import session (creates new PricingDiscount)
      setImportPhase('starting');

      const startResult = await startPricingImport({
        partnerId: partner.id,
        pricingType,
      });
      const { pricingDiscountId } = startResult;

      // Phase 2: Add rules to the import session in batches of 50
      setImportPhase('importing');

      const BATCH_SIZE = 50;
      let totalCreated = 0;
      let totalErrors = 0;

      for (let i = 0; i < validRules.length; i += BATCH_SIZE) {
        const batch = validRules.slice(i, i + BATCH_SIZE);
        const result = await addToPricingImport({
          partnerId: partner.id,
          pricingDiscountId,
          rules: batch,
        });
        totalCreated += result.created ?? 0;
        totalErrors += (result.errors ?? []).filter(e => e != null).length;
      }

      setImportPhase('complete');
      setImportResult({ created: totalCreated, errors: totalErrors });

      if (totalCreated > 0) {
        notify(`Imported ${totalCreated} pricing rules`, { type: 'success' });
        refresh();
      }
      if (totalErrors > 0) {
        notify(`${totalErrors} rules failed to import`, { type: 'warning' });
      }
    } catch (error) {
      // importPhase is not in useCallback dependency (adding it would recreate the callback
      // on every phase change). The phase at error time is deterministic: 'starting' if
      // startPricingImport threw, 'importing' if addToPricingImport threw.
      console.error('ConditionDiscountsImportDialog: import failed', {
        pricingType,
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Import failed';
      notify(`Error: ${message}`, { type: 'error' });
      setImportPhase('idle');
    }
  }, [parsedRules, notify, pricingType, refresh]);

  const validRulesCount = parsedRules.filter(r => !r._hasError).length;
  const invalidRulesCount = parsedRules.length - validRulesCount;

  const displayedRules = useMemo(() => {
    if (showErrorsOnly) {
      return parsedRules.filter(r => r._hasError);
    }
    return parsedRules.slice(0, 100); // Limit preview to 100 rows
  }, [parsedRules, showErrorsOnly]);

  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Pricing Rules</DialogTitle>
      <DialogContent>
        {/* Step 1: File Upload */}
        {!file && importPhase === 'idle' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Import Pricing Rules</AlertTitle>
              Importing will create a new pricing rule set. Once imported, you can initiate
              pricing to apply these rules to your catalog.
            </Alert>
            <Typography variant="body1" gutterBottom>
              Upload a TSV file with columns:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isConditionType ?
                'condition, department, category, merchant_category, primary_tag, primary_name, discount' :
                'source_column, department, category, merchant_category, upc, primary_tag, primary_name, secondary_tag, secondary_name, tertiary_tag, tertiary_name, discount'}
            </Typography>
            <Button variant="contained" component="label">
              Choose File
              <input type="file" hidden accept=".tsv,.txt,.csv" onChange={handleFileChange} />
            </Button>
          </Box>
        )}

        {/* Step 2: Preview */}
        {file && parsedRules.length > 0 && importPhase === 'idle' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Ready to Import</AlertTitle>
              Clicking &quot;Import&quot; will create a new pricing rule set with
              the 
              {' '}
              {validRulesCount}
              {' '}
              valid rules from your file.
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Preview</Typography>
              <Chip label={`${validRulesCount} valid`} color="success" size="small" />
              {invalidRulesCount > 0 && (
                <Chip
                  label={`${invalidRulesCount} invalid`}
                  color="error"
                  size="small"
                  onClick={() => setShowErrorsOnly(!showErrorsOnly)}
                  clickable
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {file.name}
              </Typography>
            </Box>

            {invalidRulesCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Validation Issues</AlertTitle>
                {invalidRulesCount}
                {' '}
                rules have errors and will be skipped during import.
                Click the &quot;invalid&quot; chip above to view only problematic rows.
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    {isConditionType ? (
                      <>
                        <TableCell>Condition</TableCell>
                        <TableCell>Dept</TableCell>
                        <TableCell>Cat</TableCell>
                        <TableCell>Merchant Cat</TableCell>
                        <TableCell>Tag</TableCell>
                        <TableCell>Name</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Source</TableCell>
                        <TableCell>Dept</TableCell>
                        <TableCell>Cat</TableCell>
                        <TableCell>Tag</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Secondary Tag</TableCell>
                        <TableCell>Secondary Name</TableCell>
                        <TableCell>Tertiary Tag</TableCell>
                        <TableCell>Tertiary Name</TableCell>
                      </>
                    )}
                    <TableCell>{isConditionType ? 'Discount (%)' : 'Discount / Price'}</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRules.map((rule, idx) => {
                    let formattedDiscount = '-';
                    if (rule.discount != null) {
                      formattedDiscount = rule.sourceColumn === 'absolute_price' ?
                        `${currencySymbol}${(rule.discount / 100).toFixed(2)}` :
                        `${rule.discount}%`;
                    }
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: rule._hasError ? 'rgba(255, 0, 0, 0.05)' : undefined,
                        }}
                      >
                        <TableCell>{rule._rowIndex}</TableCell>
                        {isConditionType ? (
                          <>
                            <TableCell>{rule.condition || <em style={{ color: '#888' }}>All</em>}</TableCell>
                            <TableCell>{rule.department || '-'}</TableCell>
                            <TableCell>{rule.category || '-'}</TableCell>
                            <TableCell>{rule.merchantCategory || '-'}</TableCell>
                            <TableCell>{rule.tag || '-'}</TableCell>
                            <TableCell>{rule.name || '-'}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{rule.sourceColumn}</TableCell>
                            <TableCell>{rule.department || '-'}</TableCell>
                            <TableCell>{rule.category || '-'}</TableCell>
                            <TableCell>{rule.tag || '-'}</TableCell>
                            <TableCell>{rule.name || '-'}</TableCell>
                            <TableCell>{rule.secondaryTag || '-'}</TableCell>
                            <TableCell>{rule.secondaryName || '-'}</TableCell>
                            <TableCell>{rule.tertiaryTag || '-'}</TableCell>
                            <TableCell>{rule.tertiaryName || '-'}</TableCell>
                          </>
                        )}
                        <TableCell>{formattedDiscount}</TableCell>
                        <TableCell>
                          {rule._errorMessage && (
                            <Tooltip title={rule._errorMessage}>
                              <Typography color="error" variant="caption" sx={{ cursor: 'help' }}>
                                {rule._errorMessage.slice(0, 30)}
                                ...
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {parsedRules.length > 100 && !showErrorsOnly && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing first 100 of
                {' '}
                {parsedRules.length}
                {' '}
                rows
              </Typography>
            )}
          </Box>
        )}

        {/* Import Progress */}
        {(importPhase === 'starting' || importPhase === 'importing') && (
          <Box sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom>Importing rules...</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress variant="indeterminate" />
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 4: Import Complete */}
        {importPhase === 'complete' && (
          <Box sx={{ py: 4 }}>
            <Alert
              severity={importResult.errors > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
            >
              <AlertTitle>Import Complete</AlertTitle>
              <Box>
                Successfully imported
                {' '}
                {importResult.created}
                {' '}
                pricing rules.
              </Box>
              {importResult.errors > 0 && (
                <Box sx={{ mt: 1 }}>
                  {importResult.errors}
                  {' '}
                  rules failed to import.
                </Box>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importPhase === 'starting' || importPhase === 'importing'}>
          Close
        </Button>
        {file && importPhase === 'idle' && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={validRulesCount === 0}
          >
            Import
            {' '}
            {validRulesCount}
            {' '}
            Rules
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConditionDiscountsImportDialog;
